import type { VaultDef } from './types';

/**
 * v2 Vault layout: wave-count = vault-tier (Alpha=1, Sigma=2, Tau=3, Phi=4, Omega=5).
 * The LAST wave of each vault is the boss-wave (single big enemy with ×3 HP + ×5 CD-drop).
 *
 * Unlock-Gates sind alle power-basiert (`min_power`) — kein Rarity-Check, kein
 * Hard-ATK/DEF-Check. Equipment-Power (siehe `calcEquippedItemPower`) reflektiert
 * Stats × Upgrade-Level × Rarity-Weight × Bonus-Stats × Ascension-Tier holistisch.
 *
 * Power-Tuning-Referenz nach 2026-05 Catalog-Cleanup:
 *   Starter L0 (alle 5):           ~19  Power
 *   Starter L5 (alle 5):           ~85  Power
 *   Starter L10 (alle 5):          ~125 Power
 *   Vault-Knight Set L0 (3 pcs):   ~121 Power
 *   Vault-Knight Set L10:          ~210 Power
 *   Set L10 + 1 Legendary L10:     ~315 Power
 *   Set L10 + 2 Legendary L10:     ~400 Power
 *   + bonus-stat rolls + ascension: 600+ Power
 *
 * Die min_power-Gates sind so gewählt, dass jede Tier-Stufe einen klaren
 * Equipment-Sprung erfordert — Starter-Upgrade reicht für T2/T3, Set-Pieces
 * werden für T4 unverzichtbar, Legendaries + Ascension für T5.
 */
export const VAULTS: VaultDef[] = [
  {
    id: 'vault_alpha',
    nameKey: 'vault_alpha',
    descriptionKey: 'vault_alpha_desc',
    minLevel: 1,
    tier: 1,
    // Kein Power-Gate — Einstiegsvault, jeder neue Spieler kann sofort rein.
    requirements: [],
    dropTier: 'common',
    // Hybrid trash + elite: 4 swarmlings (1-shot kills, horde feel) + 2 elites
    // (longer fight, HP-bar visible).
    waves: [
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'security_bot_mk1', 'glitch_drone'] },
    ],
  },
  {
    id: 'vault_sigma',
    nameKey: 'vault_sigma',
    descriptionKey: 'vault_sigma_desc',
    minLevel: 5,
    tier: 2,
    // Sanftes Gate: Starter-Kit auf ~L3 oder 1-2 erste Drops aus Alpha.
    requirements: [{ type: 'min_power', value: 60 }],
    dropTier: 'uncommon',
    waves: [
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'patrol_drone', 'glitch_drone'] },
      // Boss wave
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'reaver_bot', 'patrol_drone'] },
    ],
  },
  {
    id: 'vault_tau',
    nameKey: 'vault_tau',
    descriptionKey: 'vault_tau_desc',
    minLevel: 10,
    tier: 3,
    // Mid-Game Gate: Starter-Kit komplett auf L5+ ODER erste Set-Stücke gefarmt.
    requirements: [{ type: 'min_power', value: 150 }],
    dropTier: 'rare',
    waves: [
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'reaver_bot', 'patrol_drone'] },
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'void_stalker', 'reaver_bot'] },
      // Boss wave
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'elite_sentinel', 'void_stalker'] },
    ],
  },
  {
    id: 'vault_phi',
    nameKey: 'vault_phi',
    descriptionKey: 'vault_phi_desc',
    minLevel: 20,
    tier: 4,
    // Late-Game Gate: Vault-Knight-Set ≥ 2 Pieces auf L5 oder erstes Legendary.
    requirements: [{ type: 'min_power', value: 300 }],
    dropTier: 'epic',
    waves: [
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'elite_sentinel', 'void_stalker'] },
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'bunker_warden', 'elite_sentinel'] },
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'reactor_titan', 'bunker_warden'] },
      // Boss wave
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'reactor_titan', 'bunker_warden'] },
    ],
  },
  {
    id: 'vault_omega',
    nameKey: 'vault_omega',
    descriptionKey: 'vault_omega_desc',
    minLevel: 35,
    tier: 5,
    // Endgame Gate: Full Set L10 + ≥ 2 Legendarys L5+ ODER frühe Ascension-Tiers.
    requirements: [{ type: 'min_power', value: 600 }],
    dropTier: 'legendary',
    waves: [
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'reactor_titan', 'bunker_warden'] },
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'nexus_apex', 'bunker_warden'] },
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'nexus_apex', 'reactor_titan'] },
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'omega_herald', 'nexus_apex'] },
      // Boss wave
      { enemies: ['swarmling', 'swarmling', 'swarmling', 'swarmling', 'omega_herald', 'nexus_apex'] },
    ],
  },
];

export function getVault(id: string): VaultDef | undefined {
  return VAULTS.find((v) => v.id === id);
}
